// AuroraConfidenceMeter — Tier-A aurora renderer for the confidence-meter component.
//
// A React port of the "AURORA DRIFT" design-exploration mockup
// (design-explorations/scenario-themes/aurora-drift/index.html), driven by the
// REAL confidence-meter data + the ConfidenceMeterStudent state machine. The
// THIRD consumer of the shared theme-world toolkit (lib/scenario-themes/) —
// validating it on a LIGHT/airy register: atmosphere, audio, sound toggle.
//
// State machine + onActivityResponse contract mirror ConfidenceMeterSlide's
// `ConfidenceMeterStudent` exactly: `currentIndex` + `ratings` Record keyed by
// item id with pre/post; on each item lock it emits the EXACT
//   { activityType:'confidence-meter', mode:'survey',
//     responseData:{ id, pre, post } }
// payload, advancing the index until all items are rated, then the summary.
//
// ★ THE STANDOUT MOMENT ★ — THE SHIFT BLOOM. Re-rating a statement blooms an
// aurora ribbon (canvas) arcing from the pre value to the post value; it swells
// in brightness/thickness and the whole mesh hue-shifts in proportion to |Δ|.
// A bigger change of heart = a brighter, longer bloom + a fuller `reveal` chime.
// Changing your mind becomes something luminous.

import { useState, useCallback, useEffect, useRef } from 'react';
import type { ConfidenceMeterSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import {
  useThemeWorldAudio,
  playAuroraPluck,
  playAuroraShiftBloom,
} from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';

type Item = ConfidenceMeterSlideData['items'][number];

interface RuntimeRating {
  pre?: number;
  post?: number;
  contentRevealed?: boolean;
  shiftShown?: boolean;
}

const STYLE_ID = 'aurora-cm-scoped-styles';

// Fixed aurora palette constants (mesh-faithful, beyond the two theme tokens).
const GREEN = '#5ef2a0';
const TEAL = '#15c4b8';
const VIOLET = '#8b6cff';
const ROSE = '#ff8fb1';

// ─────────────────────────────────────────────────────────────────────────────
// Component-specific scoped CSS — glass panels, luminous slider, evidence bloom,
// shift reveal. Namespaced under `.aurcm`; injected once, ref-counted.
// (Atmosphere mesh/bokeh/stars live in the toolkit's <ThemeWorldAtmosphere>.)
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(): string {
  return `
.aurcm { position: relative; }
.aurcm * { box-sizing: border-box; }

/* the whole mesh blooms briefly when a shift is revealed */
.aurcm .aurcm-mesh { transition: filter 2.4s cubic-bezier(0.22,0.61,0.27,1); }

/* ===== GLASS PANELS ===== */
.aurcm .aur-glass {
  background: rgba(20,30,60,0.34);
  backdrop-filter: blur(22px) saturate(1.5);
  -webkit-backdrop-filter: blur(22px) saturate(1.5);
  border: 1px solid rgba(234,244,255,0.16);
  border-radius: 26px;
  box-shadow: 0 18px 60px rgba(0,0,0,0.34), inset 0 1px 0 rgba(234,244,255,0.07), inset 0 0 60px rgba(94,242,160,0.02);
  position: relative; overflow: hidden;
}
.aurcm .aur-glass::before {
  content: ""; position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(120% 60% at 50% -10%, rgba(234,244,255,0.10), transparent 60%);
}

/* statement entrance */
.aurcm .aur-stmt { opacity: 0; transform: translateY(40px); transition: opacity 1.1s cubic-bezier(0.22,0.61,0.27,1), transform 1.1s cubic-bezier(0.22,0.61,0.27,1), filter 0.9s; }
.aurcm .aur-stmt.aur-in { opacity: 1; transform: translateY(0); }
.aurcm .aur-stmt.aur-past { opacity: 0.5; filter: saturate(0.7); }

/* ===== LUMINOUS SLIDER ===== */
.aurcm input[type="range"].aur-range {
  -webkit-appearance: none; appearance: none;
  width: 100%; height: 8px; border-radius: 999px; outline: none; cursor: grab;
  border: 1px solid rgba(234,244,255,0.10);
}
.aurcm input[type="range"].aur-range:active { cursor: grabbing; }
.aurcm input[type="range"].aur-range:disabled { cursor: default; opacity: 0.85; }
.aurcm input[type="range"].aur-range::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none;
  width: 26px; height: 26px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #fff, ${GREEN} 55%, ${TEAL});
  border: 2px solid rgba(255,255,255,0.85);
  box-shadow: 0 0 0 6px rgba(94,242,160,0.18), 0 0 22px rgba(94,242,160,0.7);
  transition: box-shadow 0.3s, transform 0.2s;
}
.aurcm input[type="range"].aur-range::-webkit-slider-thumb:hover { transform: scale(1.12); }
.aurcm input[type="range"].aur-range::-moz-range-thumb {
  width: 26px; height: 26px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.85);
  background: radial-gradient(circle at 35% 30%, #fff, ${GREEN} 55%, ${TEAL});
  box-shadow: 0 0 0 6px rgba(94,242,160,0.18), 0 0 22px rgba(94,242,160,0.7);
}

/* lock button */
.aurcm .aur-lock {
  margin-top: 26px; display: inline-flex; align-items: center; gap: 10px;
  font-size: 0.92rem; font-weight: 500; letter-spacing: 0.02em;
  color: #0a0e1f; cursor: pointer;
  padding: 13px 26px; border-radius: 999px; border: none;
  background: linear-gradient(110deg, ${GREEN}, ${TEAL});
  box-shadow: 0 6px 28px rgba(21,196,184,0.45);
  transition: transform 0.35s cubic-bezier(0.22,0.61,0.27,1), box-shadow 0.35s, opacity 0.4s;
}
.aurcm .aur-lock:hover { transform: translateY(-2px); box-shadow: 0 10px 38px rgba(94,242,160,0.55); }
.aurcm .aur-lock:disabled { opacity: 0.4; pointer-events: none; }

/* ===== EVIDENCE BLOOM ===== */
.aurcm .aur-evidence {
  margin-top: 30px; max-height: 0; opacity: 0; overflow: hidden;
  transition: max-height 1s cubic-bezier(0.22,0.61,0.27,1), opacity 0.9s cubic-bezier(0.22,0.61,0.27,1), margin-top 0.6s;
}
.aurcm .aur-evidence.aur-open { max-height: 720px; opacity: 1; }
.aurcm .aur-evidence-inner {
  padding: 26px 28px; border-radius: 18px;
  background: rgba(139,108,255,0.10);
  border: 1px solid rgba(139,108,255,0.28);
  box-shadow: inset 0 0 40px rgba(139,108,255,0.10);
  position: relative;
}
.aurcm .aur-evidence-inner::after {
  content: ""; position: absolute; inset: 0; border-radius: 18px; pointer-events: none;
  background: radial-gradient(80% 100% at 0% 0%, rgba(94,242,160,0.10), transparent 50%);
}

/* ===== SHIFT REVEAL ===== */
.aurcm .aur-shift {
  margin-top: 30px; opacity: 0; transform: translateY(20px);
  transition: opacity 0.9s cubic-bezier(0.22,0.61,0.27,1), transform 0.9s cubic-bezier(0.22,0.61,0.27,1);
}
.aurcm .aur-shift.aur-show { opacity: 1; transform: translateY(0); }
.aurcm .aur-shift-canvas-wrap {
  position: relative; height: 150px; border-radius: 18px;
  background: rgba(10,14,31,0.4); border: 1px solid rgba(234,244,255,0.10); overflow: hidden;
}
.aurcm .aur-shift-canvas-wrap canvas { display: block; width: 100%; height: 100%; }

/* ===== REDUCED MOTION ===== */
@media (prefers-reduced-motion: reduce) {
  .aurcm .aurcm-mesh { transition: none !important; }
  .aurcm .aur-stmt, .aurcm .aur-evidence, .aurcm .aur-shift, .aurcm .aur-lock { transition-duration: 0.001s !important; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // CSS is static; intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Confidence as a 0..1 fraction of an item's scale (for pitch + canvas mapping). */
function fracOf(item: Item, v: number): number {
  const span = item.scale.max - item.scale.min;
  return span > 0 ? (v - item.scale.min) / span : 0.5;
}

function midOf(item: Item): number {
  return Math.round((item.scale.min + item.scale.max) / 2);
}

/** The delta phrasing (mockup-faithful, scaled to the item's own range). */
function shiftPhrase(item: Item, pre: number, post: number): string {
  const span = item.scale.max - item.scale.min || 1;
  const dNorm = ((post - pre) / span) * 100; // normalise to a 0..100-ish feel
  if (dNorm < -25) return 'your confidence softened — the evidence reached you';
  if (dNorm < -5) return 'your confidence eased a little';
  if (dNorm > 25) return 'the evidence confirmed and deepened your sense';
  if (dNorm > 5) return 'your confidence rose gently';
  return "you held steady — and that's fine too";
}

/**
 * drawRibbon — the aurora arc from `before` to `after` on a canvas.
 * A glowing band sweeps across; brightness/thickness scale with the magnitude
 * of the shift. `animate` runs the sweep; otherwise it draws the final frame.
 * Returns a cancel fn for the rAF loop.
 */
function drawRibbon(
  canvas: HTMLCanvasElement,
  item: Item,
  before: number,
  after: number,
  animate: boolean,
): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => undefined;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = canvas.clientWidth || canvas.parentElement?.clientWidth || 300;
  const cssH = canvas.clientHeight || 90;
  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const fBefore = fracOf(item, before) * 100;
  const fAfter = fracOf(item, after) * 100;
  const mag = Math.abs(fAfter - fBefore);
  const W = cssW;
  const H = cssH;
  const padX = 26;
  const x0 = padX;
  const x1 = W - padX;
  // 0% conf → bottom, 100% → top
  const yOf = (v: number) => H - 16 - (v / 100) * (H - 32);
  const yB = yOf(fBefore);
  const yA = yOf(fAfter);

  const stops: Array<[number, string]> = [
    [0.0, GREEN],
    [0.4, TEAL],
    [0.75, VIOLET],
    [1.0, ROSE],
  ];

  let prog = animate ? 0 : 1;
  let raf = 0;

  const frame = () => {
    ctx.clearRect(0, 0, W, H);

    // baseline ghost markers
    ctx.save();
    ctx.globalAlpha = 0.5;
    [
      [x0, yB],
      [x1, yA],
    ].forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fillStyle = '#eaf4ff';
      ctx.fill();
    });
    ctx.restore();

    const p = prog;
    const cx = x0 + (x1 - x0) * p;
    const cy = yB + (yA - yB) * p;

    const grad = ctx.createLinearGradient(x0, 0, x1, 0);
    stops.forEach(([o, c]) => grad.addColorStop(o, c));

    const midX = (x0 + cx) / 2;
    const bow = (yA - yB) * 0.25 - 18 - mag * 0.18;
    const midY = (yB + cy) / 2 + bow;

    // soft glow underlay
    ctx.save();
    ctx.lineCap = 'round';
    const baseW = 3 + mag * 0.1;
    ctx.shadowBlur = 16 + mag * 0.35;
    ctx.shadowColor = GREEN;
    ctx.strokeStyle = grad;
    ctx.globalAlpha = 0.85;
    ctx.lineWidth = baseW + 6;
    ctx.beginPath();
    ctx.moveTo(x0, yB);
    ctx.quadraticCurveTo(midX, midY, cx, cy);
    ctx.stroke();
    // bright core
    ctx.shadowBlur = 8 + mag * 0.2;
    ctx.globalAlpha = 1;
    ctx.lineWidth = baseW;
    ctx.beginPath();
    ctx.moveTo(x0, yB);
    ctx.quadraticCurveTo(midX, midY, cx, cy);
    ctx.stroke();
    ctx.restore();

    // moving head bloom
    ctx.save();
    const headR = 6 + mag * 0.06;
    const rg = ctx.createRadialGradient(cx, cy, 0, cx, cy, headR * 3);
    rg.addColorStop(0, 'rgba(255,255,255,0.95)');
    rg.addColorStop(0.3, 'rgba(94,242,160,0.8)');
    rg.addColorStop(1, 'rgba(94,242,160,0)');
    ctx.fillStyle = rg;
    ctx.beginPath();
    ctx.arc(cx, cy, headR * 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // labels
    ctx.fillStyle = 'rgba(234,244,255,0.55)';
    ctx.font = '11px "Hanken Grotesk", sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`${before}`, x0 - 4, yB - 12);
    if (p > 0.92) {
      ctx.textAlign = 'right';
      ctx.fillText(`${after}`, x1 + 4, yA - 12);
    }

    if (prog < 1) {
      prog += 0.025;
      raf = requestAnimationFrame(frame);
    }
  };
  frame();

  return () => {
    if (raf) cancelAnimationFrame(raf);
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function AuroraConfidenceMeter({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: ConfidenceMeterSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  // savedResponse carries only the last item's payload ({ id, pre, post }), which
  // can't reconstruct the full multi-item pre/post state machine — accept the
  // prop but skip rehydration (mirrors the standard ConfidenceMeterSlide path).
  void savedResponse;
  const reduceMotion = usePrefersReducedMotion();
  useScopedStyles();

  const headingFont = theme.fonts.heading;
  const bodyFont = theme.fonts.body;
  const frost = theme.colors.text; // #eaf4ff
  const frostDim = theme.colors.textMuted; // #9fb3d9

  // ── Audio (toolkit) ──────────────────────────────────────────────────────────
  const { enabled: audioOn, toggle: toggleAudio, play, playWith } = useThemeWorldAudio('aurora');

  // ── State machine (mirrors ConfidenceMeterStudent exactly) ───────────────────
  const total = data.items.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ratings, setRatings] = useState<Record<string, RuntimeRating>>({});

  const completedCount = Object.values(ratings).filter((r) => r.post !== undefined).length;
  const allDone = completedCount === total && total > 0;

  // Fire onComplete exactly once when the LAST item's POST rating is locked
  // (the whole set is done) — not per item.
  const completedRef = useRef(false);

  const setRating = (id: string, patch: Partial<RuntimeRating>) =>
    setRatings((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const lockPre = useCallback(
    (id: string) => {
      play('commit'); // warm two-note bell
      setRatings((prev) => ({ ...prev, [id]: { ...prev[id], contentRevealed: true } }));
    },
    [play],
  );

  const lockPost = useCallback(
    (id: string, isLast: boolean) => {
      setRatings((prev) => {
        const r = prev[id];
        if (!r || r.pre === undefined || r.post === undefined) return prev;
        onActivityResponse?.({
          activityType: 'confidence-meter',
          mode: 'survey',
          responseData: { id, pre: r.pre, post: r.post },
        });
        return { ...prev, [id]: { ...r, shiftShown: true } };
      });
      if (isLast) {
        // Terminal point: last item's post rating locked — the whole set is done.
        if (!completedRef.current) {
          completedRef.current = true;
          onComplete?.();
        }
      } else {
        // brief pause so the current shift bloom can land before the next item rises
        const delay = reduceMotion ? 0 : 1100;
        window.setTimeout(() => setCurrentIndex((idx) => idx + 1), delay);
      }
    },
    [onActivityResponse, onComplete, reduceMotion],
  );

  // ── Per-item canvas refs + the shift bloom (THE STANDOUT) ────────────────────
  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const meshRef = useRef<HTMLDivElement | null>(null);
  const cancelRefs = useRef<Map<string, () => void>>(new Map());
  const meshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const cancels = cancelRefs.current;
    const timer = meshTimer.current;
    return () => {
      cancels.forEach((c) => c());
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Whenever an item's shift becomes shown, bloom: draw the ribbon + fire the
  // magnitude-scaled chime + briefly hue-shift the whole aurora mesh.
  const bloom = useCallback(
    (item: Item, pre: number, post: number) => {
      const canvas = canvasRefs.current.get(item.id);
      if (canvas) {
        cancelRefs.current.get(item.id)?.();
        const cancel = drawRibbon(canvas, item, pre, post, !reduceMotion);
        cancelRefs.current.set(item.id, cancel);
      }

      const span = item.scale.max - item.scale.min || 1;
      const magNorm = (Math.abs(post - pre) / span) * 100; // 0..100-ish
      // chime intensity scales with how far the mind moved
      playWith((e) => playAuroraShiftBloom(e, Math.min(1.5, magNorm / 40)));

      const mesh = meshRef.current;
      if (mesh && !reduceMotion) {
        const dNorm = ((post - pre) / span) * 100;
        mesh.style.filter = `saturate(${1.5 + magNorm / 100}) brightness(${1.2 + magNorm / 250}) hue-rotate(${dNorm * 0.8}deg)`;
        if (meshTimer.current) clearTimeout(meshTimer.current);
        meshTimer.current = setTimeout(() => {
          if (meshRef.current) meshRef.current.style.filter = '';
        }, 3200);
      }
    },
    [reduceMotion, playWith],
  );

  // On completion, a gentle resolving chord (advance cue = the completion chord).
  const completedFiredRef = useRef(false);
  useEffect(() => {
    if (allDone && !completedFiredRef.current) {
      completedFiredRef.current = true;
      const t = setTimeout(() => play('advance'), 400);
      return () => clearTimeout(t);
    }
    if (!allDone) completedFiredRef.current = false;
  }, [allDone, play]);

  return (
    <div
      className="aurcm h-full w-full overflow-y-auto"
      style={{
        background: theme.colors.background,
        color: frost,
        fontFamily: bodyFont,
      }}
    >
      {/* ===== ATMOSPHERE (toolkit) — wrapped so we can hue-shift the mesh ===== */}
      <div ref={meshRef} className="aurcm-mesh" style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
        <ThemeWorldAtmosphere
          worldId="aurora"
          primary={TEAL}
          accent={VIOLET}
          background={theme.colors.background}
        />
      </div>

      {/* ===== SOUND TOGGLE (toolkit) ===== */}
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 10 }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '88px 24px 120px' }}>
          {/* ===== HERO ===== */}
          <header style={{ textAlign: 'center', marginBottom: 64 }}>
            <div
              style={{
                fontSize: '0.74rem',
                letterSpacing: '0.42em',
                textTransform: 'uppercase',
                color: TEAL,
                fontWeight: 500,
                marginBottom: 22,
              }}
            >
              Aurora Drift · Metacognition
            </div>
            <h1
              style={{
                fontFamily: headingFont,
                fontWeight: 400,
                fontSize: 'clamp(2.2rem, 6vw, 3.8rem)',
                lineHeight: 1.06,
                letterSpacing: '-0.01em',
                margin: '0 0 20px',
                color: frost,
              }}
            >
              {data.title}
            </h1>
            {data.subtitle && (
              <p style={{ fontSize: '1.12rem', color: frostDim, maxWidth: '50ch', margin: '0 auto', fontWeight: 300 }}>
                {data.subtitle}
              </p>
            )}
          </header>

          {/* ===== STATEMENTS ===== */}
          {data.items.map((item, idx) => {
            if (idx > currentIndex) return null;
            const r = ratings[item.id] ?? {};
            const isCurrent = idx === currentIndex;
            const preLocked = !!r.contentRevealed;
            const postLocked = r.post !== undefined;
            const isLast = idx === total - 1;
            const preVal = r.pre ?? midOf(item);
            const postVal = r.post ?? r.pre ?? midOf(item);

            return (
              <section
                key={item.id}
                className={`aur-glass aur-stmt aur-in${postLocked && !isCurrent ? ' aur-past' : ''}`}
                data-confidence-id={item.id}
                style={{ padding: '40px 38px 44px', marginBottom: 48 }}
              >
                <div style={{ fontFamily: headingFont, fontStyle: 'italic', fontSize: '1.4rem', color: TEAL, marginBottom: 6 }}>
                  Belief {String(idx + 1).padStart(2, '0')}
                </div>
                <h3
                  style={{
                    fontFamily: headingFont,
                    fontWeight: 400,
                    fontSize: 'clamp(1.5rem, 3.6vw, 2.1rem)',
                    lineHeight: 1.18,
                    margin: '0 0 6px',
                    letterSpacing: '-0.005em',
                    color: frost,
                  }}
                >
                  <span style={{ color: frostDim }}>“</span>
                  {item.statement}
                  <span style={{ color: frostDim }}>”</span>
                </h3>

                {/* ── PRE rating ── */}
                {!preLocked && isCurrent && (
                  <AuroraRating
                    label="First — how sure are you?"
                    item={item}
                    value={preVal}
                    onChange={(v) => setRating(item.id, { pre: v })}
                    onLock={() => {
                      setRating(item.id, { pre: preVal });
                      lockPre(item.id);
                    }}
                    lockLabel="Lock it in & see the evidence →"
                    playPluck={(frac) => playWith((e) => playAuroraPluck(e, frac))}
                  />
                )}

                {/* ── EVIDENCE bloom ── */}
                <div className={`aur-evidence${preLocked ? ' aur-open' : ''}`}>
                  <div className="aur-evidence-inner">
                    <div
                      style={{
                        fontSize: '0.7rem',
                        letterSpacing: '0.26em',
                        textTransform: 'uppercase',
                        color: VIOLET,
                        fontWeight: 600,
                        marginBottom: 10,
                      }}
                    >
                      What the science says
                    </div>
                    <p style={{ fontSize: '1.05rem', color: frost, fontWeight: 300, lineHeight: 1.62, margin: 0 }}>
                      {item.content}
                    </p>
                  </div>

                  {/* ── POST rating ── */}
                  {preLocked && r.post === undefined && (
                    <div style={{ marginTop: 30 }}>
                      <AuroraRating
                        label="Now — how sure are you?"
                        item={item}
                        value={postVal}
                        onChange={(v) => setRating(item.id, { post: v })}
                        onLock={() => {
                          const finalPost = r.post ?? r.pre ?? midOf(item);
                          setRating(item.id, { post: finalPost });
                          bloom(item, r.pre ?? midOf(item), finalPost);
                          lockPost(item.id, isLast);
                        }}
                        lockLabel="Reveal the shift ✦"
                        playPluck={(frac) => playWith((e) => playAuroraPluck(e, frac))}
                      />
                    </div>
                  )}

                  {/* ── THE SHIFT (hero) — aurora ribbon bloom ── */}
                  <div className={`aur-shift${r.shiftShown ? ' aur-show' : ''}`}>
                    <div className="aur-shift-canvas-wrap">
                      <canvas
                        ref={(el) => {
                          if (el) canvasRefs.current.set(item.id, el);
                          else canvasRefs.current.delete(item.id);
                        }}
                      />
                    </div>
                    {r.shiftShown && r.pre !== undefined && r.post !== undefined && (
                      <ShiftReadout item={item} pre={r.pre} post={r.post} />
                    )}
                  </div>
                </div>
              </section>
            );
          })}

          {/* ===== CLOSING ===== */}
          {allDone && data.summary?.prompt && (
            <section className="aur-glass aur-stmt aur-in" style={{ padding: '46px 40px 50px', textAlign: 'center' }}>
              <h2
                style={{
                  fontFamily: headingFont,
                  fontWeight: 400,
                  fontSize: 'clamp(1.9rem, 5vw, 2.7rem)',
                  margin: '0 0 10px',
                  letterSpacing: '-0.01em',
                  color: frost,
                }}
              >
                What shifted, and why?
              </h2>
              <p style={{ color: frostDim, fontWeight: 300, maxWidth: '46ch', margin: '0 auto 34px' }}>
                The brighter the ribbon, the more your mind moved.
              </p>

              {/* before→after arcs as glowing ribbons */}
              <ClosingRibbons items={data.items} ratings={ratings} frost={frost} frostDim={frostDim} headingFont={headingFont} />

              <div
                style={{
                  padding: '24px 26px',
                  borderRadius: 18,
                  background: 'rgba(139,108,255,0.08)',
                  border: '1px solid rgba(139,108,255,0.24)',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: '0.7rem', letterSpacing: '0.26em', textTransform: 'uppercase', color: VIOLET, fontWeight: 600, marginBottom: 12 }}>
                  A gentle reflection
                </div>
                <p style={{ fontFamily: headingFont, fontSize: '1.25rem', fontStyle: 'italic', color: frost, lineHeight: 1.4, margin: 0 }}>
                  {data.summary.prompt}
                </p>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function AuroraRating({
  label,
  item,
  value,
  onChange,
  onLock,
  lockLabel,
  playPluck,
}: {
  label: string;
  item: Item;
  value: number;
  onChange: (v: number) => void;
  onLock: () => void;
  lockLabel: string;
  playPluck: (frac: number) => void;
}) {
  const lastTick = useRef(value);
  const frac = fracOf(item, value);
  const pct = Math.round(frac * 100);

  const fill = `linear-gradient(90deg, rgba(94,242,160,0.9) 0%, rgba(21,196,184,0.85) ${pct}%, rgba(234,244,255,0.12) ${pct}%, rgba(234,244,255,0.10) 100%)`;

  return (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          fontSize: '0.72rem',
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: 'rgba(234,244,255,0.4)',
          margin: '6px 0 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: TEAL, boxShadow: `0 0 10px ${TEAL}` }} />
        {label}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 8, marginBottom: 18 }}>
        <span
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontSize: '3.2rem',
            lineHeight: 1,
            color: '#eaf4ff',
            textShadow: '0 0 28px rgba(94,242,160,0.55)',
          }}
        >
          {value}
        </span>
      </div>

      <input
        type="range"
        className="aur-range"
        min={item.scale.min}
        max={item.scale.max}
        value={value}
        style={{ background: fill }}
        aria-label={label}
        onChange={(e) => {
          const v = Number(e.target.value);
          onChange(v);
          if (Math.abs(v - lastTick.current) >= Math.max(1, (item.scale.max - item.scale.min) / 16)) {
            playPluck(fracOf(item, v));
            lastTick.current = v;
          }
        }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 12,
          fontSize: '0.75rem',
          color: 'rgba(234,244,255,0.4)',
          letterSpacing: '0.02em',
        }}
      >
        {item.scale.labels.map((labelText, i) => (
          <span key={i} style={{ textAlign: i === 0 ? 'left' : i === item.scale.labels.length - 1 ? 'right' : 'center', flex: 1 }}>
            {labelText}
          </span>
        ))}
      </div>

      <button type="button" className="aur-lock" onClick={onLock}>
        {lockLabel}
      </button>
    </div>
  );
}

function ShiftReadout({ item, pre, post }: { item: Item; pre: number; post: number }) {
  const delta = post - pre;
  const mag = Math.abs(delta);
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±';
  return (
    <div style={{ textAlign: 'center', marginTop: 16 }}>
      <div
        style={{
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontSize: '2rem',
          fontStyle: 'italic',
          background: `linear-gradient(100deg, ${GREEN}, ${TEAL}, ${VIOLET})`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {sign}
        {mag}
      </div>
      <div style={{ fontSize: '0.98rem', color: 'rgba(234,244,255,0.62)', fontWeight: 300, marginTop: 4 }}>
        {shiftPhrase(item, pre, post)}
      </div>
    </div>
  );
}

function ClosingRibbons({
  items,
  ratings,
  frost,
  frostDim,
  headingFont,
}: {
  items: ConfidenceMeterSlideData['items'];
  ratings: Record<string, RuntimeRating>;
  frost: string;
  frostDim: string;
  headingFont: string;
}) {
  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      items.forEach((item) => {
        const r = ratings[item.id];
        const canvas = canvasRefs.current.get(item.id);
        if (canvas && r?.pre !== undefined && r?.post !== undefined) {
          drawRibbon(canvas, item, r.pre, r.post, false);
        }
      });
    });
    return () => cancelAnimationFrame(raf);
    // redraw once mounted; ratings are final at the closing panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: items.length > 1 ? 'repeat(auto-fit, minmax(220px, 1fr))' : '1fr',
        gap: 22,
        marginBottom: 38,
      }}
    >
      {items.map((item) => {
        const r = ratings[item.id];
        if (!r || r.pre === undefined || r.post === undefined) return null;
        const delta = r.post - r.pre;
        const mag = Math.abs(delta);
        const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±';
        return (
          <div
            key={item.id}
            style={{
              padding: '20px 18px 18px',
              borderRadius: 18,
              background: 'rgba(234,244,255,0.04)',
              border: '1px solid rgba(234,244,255,0.10)',
              textAlign: 'left',
            }}
          >
            <div style={{ fontSize: '0.9rem', color: frostDim, marginBottom: 14, lineHeight: 1.4, minHeight: '2.5em' }}>
              “{item.statement}”
            </div>
            <canvas
              ref={(el) => {
                if (el) canvasRefs.current.set(item.id, el);
                else canvasRefs.current.delete(item.id);
              }}
              style={{ display: 'block', width: '100%', height: 90, borderRadius: 12 }}
            />
            <div style={{ fontFamily: headingFont, fontStyle: 'italic', fontSize: '1.3rem', marginTop: 12, color: frost }}>
              {sign}
              {mag}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'rgba(234,244,255,0.4)', marginTop: 2 }}>
              {r.pre} → {r.post}
            </div>
          </div>
        );
      })}
    </div>
  );
}
